import { useRef, useState } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../lib/firebase";
import { api, callableErrorMessage } from "../lib/api";

type Props = {
  tenantId: string;
  currentLogoUrl?: string | null;
  fallbackColor?: string;
};

const MAX_BYTES = 5 * 1024 * 1024;

export function LogoUploader({ tenantId, currentLogoUrl, fallbackColor = "#4a6921" }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(currentLogoUrl ?? null);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setFeedback("✗ La imagen pesa más de 5 MB. Prueba con una más liviana.");
      return;
    }
    upload(f);
  };

  const upload = async (file: File) => {
    setBusy(true);
    setFeedback(null);
    try {
      const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
      const path = `tenants/${tenantId}/branding/logo.${ext}`;
      await uploadBytes(ref(storage, path), file, { contentType: file.type });
      const res = await api.setTenantLogo({ tenantId, storagePath: path });
      setPreview(res.data.logoUrl);
      setFeedback("✓ Logo guardado");
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.setTenantLogo({ tenantId, storagePath: null });
      setPreview(null);
      setFeedback("✓ Logo eliminado");
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <div
          className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl ring-2 ring-leaf-100"
          style={{ backgroundColor: preview ? "#fff" : fallbackColor }}
        >
          {preview ? (
            <img src={preview} alt="Logo" className="h-full w-full object-contain" />
          ) : (
            <span className="font-display text-2xl font-bold text-white">
              {tenantId.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-leaf-600">Logo del comercio</p>
          <p className="mt-1 text-sm text-leaf-700">
            PNG o JPG, fondo transparente recomendado. Máx 5 MB.
          </p>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
        onChange={pick}
        className="hidden"
      />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-primary flex-1 disabled:opacity-50"
        >
          {busy ? "Subiendo…" : preview ? "Cambiar logo" : "Subir logo"}
        </button>
        {preview && (
          <button
            type="button"
            onClick={removeLogo}
            disabled={busy}
            className="btn-ghost px-3 py-2 text-sm disabled:opacity-50"
          >
            Eliminar
          </button>
        )}
      </div>

      {feedback && (
        <p
          className={`rounded-xl px-3 py-2 text-xs ${
            feedback.startsWith("✓")
              ? "bg-leaf-50 text-leaf-800"
              : "bg-red-50 text-red-700"
          }`}
        >
          {feedback}
        </p>
      )}
    </div>
  );
}
